/* eslint-disable no-console */
import axios from 'axios';
import { unwrapPayload } from '../../api/unwrap';
import {
  filterRespondItemsByMitra,
  hasUnreadRespondsForRole,
  normalizeRespondClauseDetail,
  normalizeRespondOperationalToleranceValidity,
  unwrapRespondListItems,
} from './helpers';
import {
  buildRespondClauseListUrl,
  buildRespondTypeResponseListUrl,
} from './listParams';

const parseClauseItems = payload => {
  const list = unwrapRespondListItems(payload)
    .map(normalizeRespondClauseDetail)
    .filter(Boolean);
  if (list.length > 0) return list;
  const single = normalizeRespondClauseDetail(unwrapPayload(payload));
  return single ? [single] : [];
};

const parseTypeResponseItems = payload => {
  const list = unwrapRespondListItems(payload)
    .map(normalizeRespondOperationalToleranceValidity)
    .filter(Boolean);
  if (list.length > 0) return list;
  const single = normalizeRespondOperationalToleranceValidity(
    unwrapPayload(payload)
  );
  return single ? [single] : [];
};

export const clearClarificationRespondCache = () => {};

const authHeaders = token => ({
  Authorization: `Bearer ${token}`,
  Accept: 'application/json',
});

export const fetchTypeResponseResponds = async ({
  token,
  procurementId,
  procurementMitraId = null,
}) => {
  if (!token || !procurementId) return [];

  try {
    const response = await axios.get(
      buildRespondTypeResponseListUrl({
        procurementId,
        procurementMitraId,
      }),
      { headers: authHeaders(token) }
    );
    const items = parseTypeResponseItems(response);

    return filterRespondItemsByMitra(items, procurementMitraId, {
      scopedQuery: Boolean(procurementMitraId),
    });
  } catch (error) {
    if (error?.response?.status === 404) return [];
    console.error('Error fetching type responses:', error);
    return [];
  }
};

export const fetchClauseResponds = async ({
  token,
  procurementId,
  procurementMitraId,
  clauseDetailIds = [],
}) => {
  if (!token || !procurementId || !procurementMitraId) return [];

  try {
    const response = await axios.get(
      buildRespondClauseListUrl({
        procurementId,
        procurementMitraId,
      }),
      { headers: authHeaders(token) }
    );
    const items = parseClauseItems(response);

    const scoped = filterRespondItemsByMitra(items, procurementMitraId, {
      scopedQuery: true,
    });

    if (!clauseDetailIds?.length) return scoped;

    const clauseIdSet = new Set(clauseDetailIds.map(String));
    return scoped.filter(item =>
      clauseIdSet.has(String(item.procurementClauseDetailId))
    );
  } catch (error) {
    if (error?.response?.status === 404) return [];
    console.error('Error fetching clause responses:', error);
    return [];
  }
};

export const fetchClarificationResponds = async ({
  token,
  procurementId,
  procurementMitraId = null,
  clauseDetailIds = [],
  includeClause = true,
  includeTypeResponse = true,
}) => {
  const [clauseItems, typeItems] = await Promise.all([
    includeClause
      ? fetchClauseResponds({
          token,
          procurementId,
          procurementMitraId,
          clauseDetailIds,
        })
      : Promise.resolve([]),
    includeTypeResponse
      ? fetchTypeResponseResponds({
          token,
          procurementId,
          procurementMitraId,
        })
      : Promise.resolve([]),
  ]);

  return [...clauseItems, ...typeItems];
};

export const checkClarificationHasUnread = async ({
  token,
  procurementId,
  procurementMitraId = null,
  clauseDetailIds = [],
  isEvaluatorView = true,
  includeClause = true,
  includeTypeResponse = true,
}) => {
  if (includeTypeResponse) {
    const typeItems = await fetchTypeResponseResponds({
      token,
      procurementId,
      procurementMitraId,
    });
    if (hasUnreadRespondsForRole(typeItems, isEvaluatorView)) return true;
  }

  if (
    includeClause &&
    procurementId &&
    procurementMitraId &&
    clauseDetailIds?.length
  ) {
    const clauseItems = await fetchClauseResponds({
      token,
      procurementId,
      procurementMitraId,
      clauseDetailIds,
    });
    if (hasUnreadRespondsForRole(clauseItems, isEvaluatorView)) return true;
  }

  return false;
};
